import { ComposibleValidatable, FieldState, FormState, ValidationResponse } from "formstate";
import { observer } from "mobx-react";
import React from "react";
import { EditInput } from "@fidget/common/components/form/editInput";
import { InputGroup } from "@fidget/common/components/form/inputGroup";
import { PhoneNumberInput } from "@fidget/common/components/phoneNumberInput/phoneNumberInput";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { unwrapFormState } from "@fidget/common/utils/unwrapFormState";
import { validateEmail, validateRequired } from "@fidget/common/utils/validatorsFormState";
import { IGuest } from "../../models/base/Guest";
import styles from "./profileEditForm.css";
import { IRoom } from "../../models/room/Room";
import { parsePhoneNumber, isSupportedCountry, isValidPhoneNumber } from "libphonenumber-js";

interface Props {
  room?: IRoom;
  initial?: IGuest;
  selectedField?: FieldName;
  requiredFields?: Array<FieldName>;
  requireContact?: boolean;
  className?: string;
  onSubmit?: (profile: ProfileData) => void;
  children?: React.ReactNode;
}

export interface ProfileData {
  firstName: string | undefined;
  lastName: string | undefined;
  email: string | undefined;
  phoneRegion: string | undefined;
  phoneNumber: string | undefined;
}

type ProfileDataFormFields = {
  firstName: FieldState<string>;
  lastName: FieldState<string>;
  email: FieldState<string>;
  phoneRegion: FieldState<string>;
  phoneNumber: FieldState<string>;
};

type PhoneFormFields = {
  phoneRegion: FieldState<string>;
  phoneNumber: FieldState<string>;
};

export type FieldName = keyof ProfileData;

type FormStateMap = {
  [F in FieldName]: ComposibleValidatable<any>;
};

@observer
export class ProfileEditForm extends React.Component<Props> {
  elements: { [F in FieldName]?: HTMLInputElement } = {};
  fields: { [F in FieldName]: FieldState<string> };
  form: FormState<ProfileDataFormFields>;
  phoneForm: FormState<PhoneFormFields>;

  constructor(props: Props) {
    super(props);
    // define state for fields
    const { firstName, lastName, email, phone } = this.props.initial || ({} as IGuest);
    const parsedNumber = phone && parsePhoneNumber(phone.number, phone.region !== "ZZ" ? phone.region : undefined as any);
    const defaultPhoneRegion =
      parsedNumber?.country || this.props.room?.currentUserStore.orgConfig?.phoneRegion || "";

    this.fields = {
      firstName: new FieldState(firstName || "").validators(
        this.validateRequiredField.bind(this, "firstName")
      ),
      lastName: new FieldState(lastName || "").validators(
        this.validateRequiredField.bind(this, "lastName")
      ),
      email: new FieldState(email || "").validators(
        this.validateRequiredField.bind(this, "email"),
        validateEmail
      ),
      phoneNumber: new FieldState((parsedNumber?.formatInternational()) || "")
        .disableAutoValidation()
        .validators(this.validateRequiredField.bind(this, "phoneNumber"), this.validatePhoneNumber),
      phoneRegion: new FieldState(defaultPhoneRegion)
    };
    this.phoneForm = new FormState({
      phoneNumber: this.fields.phoneNumber,
      phoneRegion: this.fields.phoneRegion
    }).validators(this.validatePhoneForm);
    this.form = new FormState(this.fields).validators(this.validateForm);
  }

  componentDidMount() {
    this.focusSelectedField();
  }

  focusSelectedField() {
    if (this.props.selectedField) {
      const element = this.elements[this.props.selectedField];
      if (element) {
        element.focus();
        element.setSelectionRange(0, element.value.length);
      }
    }
  }

  /**
   * Validate form across all fields.
   */
  validateForm = (fields: FormStateMap): ValidationResponse => {
    if (
      this.props.requireContact &&
      !(fields.email.$ || fields.phoneNumber.$ || fields.phoneRegion.$)
    ) {
      return intl.formatMessage("calendar.meeting.error.profileContactRequired");
    }
    return undefined;
  };

  /**
   * Generic required validator.
   * Checks if field should be required based on the `requiredFields` prop
   * @param fieldName  field identifier, use Function.bind to assign in FieldState.validators()
   * @param value  value passed by field state
   */
  validateRequiredField(fieldName: FieldName, value: string): ValidationResponse {
    const { requiredFields } = this.props;
    if (requiredFields && requiredFields.indexOf(fieldName) !== -1) {
      return validateRequired(value);
    }
    return undefined;
  }

  validatePhoneForm = (fields: PhoneFormFields) => {
    const { phoneNumber, phoneRegion } = fields;

    if (phoneRegion.$ && phoneNumber.$) {
      const parsedPhoneNumber = parsePhoneNumber(phoneNumber.$, {
        defaultCountry: isSupportedCountry(phoneRegion.$) ? phoneRegion.$ : undefined
      });

      if (!parsedPhoneNumber) {
        return "";
      }
      if (!parsedPhoneNumber.isValid()) {
        phoneNumber.setError(intl.formatMessage("phone.badFormat"));
        return intl.formatMessage("phone.badFormat");
      }
    }
    return "";
  };

  validatePhoneRegion = (phoneRegion: string | undefined): ValidationResponse => {
    return !phoneRegion || !isSupportedCountry(phoneRegion)
      ? intl.formatMessage("profile.edit.missingCountryCode")
      : "";
  };

  validatePhoneNumber = (phoneNumber: string | undefined): ValidationResponse => {
    if (!phoneNumber) {
      return "";
    }
    return !isValidPhoneNumber(phoneNumber) ? intl.formatMessage("profile.edit.missingPhone") : "";
  };

  trimField = (fieldName: FieldName) => {
    const field = this.fields[fieldName];
    if (field && field.value && typeof field.value === "string") {
      field.onChange(field.value.trim());
    }
  };

  handleSubmit = async (e: React.FormEvent<EventTarget>) => {
    e.preventDefault();
    const { onSubmit } = this.props;
    const result = await this.form.validate();
    const resultPhone = await this.phoneForm.validate();
    if (onSubmit && !result.hasError && !resultPhone.hasError) {
      onSubmit(unwrapFormState(result.value, true) as ProfileData);
    }
  };

  render() {
    const { form, fields } = this;
    const { children, className } = this.props;
    return (
      <form className={className} onSubmit={this.handleSubmit}>
        <InputGroup>
          <EditInput
            value={fields.firstName.value}
            onChange={(ev) => fields.firstName.onChange(ev.currentTarget.value)}
            hasError={fields.firstName.hasError}
            errorHint={fields.firstName.error}
            inputRef={(el) => {
              this.elements.firstName = el as HTMLInputElement;
            }}
            onBlur={() => this.trimField("firstName")}
            placeholder={intl.formatMessage("profile.edit.placeholder.firstName")}
            className={styles.name}
            maxLength={64}
            id="first_name_input"
          />
        </InputGroup>
        <InputGroup>
          <EditInput
            value={fields.lastName.value}
            onChange={(ev) => fields.lastName.onChange(ev.currentTarget.value)}
            hasError={fields.lastName.hasError}
            errorHint={fields.lastName.error}
            inputRef={(el) => {
              this.elements.lastName = el as HTMLInputElement;
            }}
            onBlur={() => this.trimField("lastName")}
            placeholder={intl.formatMessage("profile.edit.placeholder.lastName")}
            className={styles.name}
            maxLength={64}
            id="last_name_input"
          />
        </InputGroup>
        <InputGroup>
          <EditInput
            value={fields.email.value}
            onChange={(ev) => fields.email.onChange(ev.currentTarget.value)}
            hasError={fields.email.hasError}
            errorHint={fields.email.error}
            inputRef={(el) => {
              this.elements.email = el as HTMLInputElement;
            }}
            onBlur={() => this.trimField("email")}
            placeholder={intl.formatMessage("profile.edit.placeholder.email")}
            className={styles.email}
            maxLength={64}
            id="email_input"
          />
        </InputGroup>
        <InputGroup>
          <fieldset onBlur={() => this.phoneForm.validate()}>
            <PhoneNumberInput
              phoneNumber={fields.phoneNumber}
              phoneRegion={fields.phoneRegion}
              inputRef={(el) => {
                this.elements.phoneRegion = el as HTMLInputElement;
              }}
              className={styles.phone}
              id="phone_number_input"
            />
          </fieldset>
        </InputGroup>

        {form.hasFormError && <div className={styles.error}>{form.formError}</div>}

        {children}
      </form>
    );
  }
}
